import { Injectable, inject } from '@angular/core';
import { IgniteSubscriptionSubscribePageType, SubscriptionStatus } from '@clubhub/ignite/models';
import { IUser } from '@shared/models';
import { AuthorizeService, ClubHubDataService } from '@shared/services';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

const subscriptionStatusNone: SubscriptionStatus = {
  active: false,
  accountOwner: false,
  accessManager: false,
  broker: false,
  numberOfLicences: null,
  trialEndDate: null,
  subscribePageType: IgniteSubscriptionSubscribePageType.NewUser,
};

@Injectable({
  providedIn: 'root',
})
export class IgniteSubscriptionStatusService {
  private readonly clubhubDataService = inject(ClubHubDataService);
  private readonly authService = inject(AuthorizeService);

  public subscriptionStatus$ = new BehaviorSubject<SubscriptionStatus>(subscriptionStatusNone);

  public constructor() {
    const authService = this.authService;

    // Monitor the user and update the subscription status when it changes (e.g. when we load the user or when their token expires)
    authService.getUser().subscribe(user => this.updateSubscriptionStatus(user));
  }

  /** Triggers a request to update the user's subscription status. */
  public async refreshSubscriptionStatus(force = false) {
    const user = await firstValueFrom(this.authService.getUser());
    await this.updateSubscriptionStatus(user, force);
    return this.subscriptionStatus$.value;
  }

  /** Updates the subscriptionStatus$ subject. If the user is null, then clears the subscription status. */
  private async updateSubscriptionStatus(user: IUser | null, force = false) {
    if (user) {
      const newStatus = await firstValueFrom(
        this.clubhubDataService.get<SubscriptionStatus>(`IgniteSubscriptionStatus?forceRefresh=${force}`, { loading: false }),
      );
      this.subscriptionStatus$.next(newStatus);
    } else {
      this.subscriptionStatus$.next(subscriptionStatusNone);
    }
  }
}
